/**
 * Menus en HTML/CSS. Chaque vue est rendue depuis un modèle ; les actions remontent par identifiant
 * (data-action) au contrôleur de l'application, qui est seul à changer d'état.
 */
import type { DockId, GameData, ShipId, SlotId } from '../data';
import { jumpTargets, shipIds, slotIds } from '../data';
import type { PilotProfile, Reward } from '../progression/profile';
import { owns } from '../progression/profile';
import { compatibleItems, slotAvailable, slotFamily } from '../sim/loadout';
import type { SessionSummary } from '../sim/telemetry/telemetry';
import { itemDeltas, loadoutStats } from './loadoutStats';
import type { Quality, Settings } from './settings';
import { SENSITIVITY_MAX, SENSITIVITY_MIN, SENSITIVITY_STEP } from './settings';

export type DockTab = 'hangar' | 'armory' | 'missions';
/** Onglets par dock : le hangar (changement d'appareil) n'existe qu'à la station. */
export const dockTabs: Record<DockId, DockTab[]> = { station: ['missions', 'armory', 'hangar'], carrier: ['missions', 'armory'] };

/** État de la mission vu depuis un dock. */
export interface DockMission {
  active: boolean;
  label: string;
  progress: string;
}

export type MenuView =
  | { kind: 'title'; player: string }
  | { kind: 'pilot'; name: string; first: boolean }
  | { kind: 'report' }
  | { kind: 'dock'; dock: DockId; zone: string; tab: DockTab; ship: ShipId; profile: PilotProfile; slot: SlotId; message: string; mission: DockMission }
  | { kind: 'paused' }
  | { kind: 'options'; settings: Settings }
  | { kind: 'help'; keys: Record<string, string> }
  | { kind: 'results'; summary: SessionSummary; ship: ShipId; mode: string; reward: Reward | null; credits: number }
  | { kind: 'confirmQuit'; missionActive: boolean }
  | { kind: 'loading'; text: string }
  | { kind: 'error'; text: string };

export type MenuAction = string;

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
const SLOT_LABEL: Record<SlotId, string> = { primary: 'Arme primaire', secondary: 'Arme secondaire', shield: 'Bouclier', engine: 'Moteur', hull: 'Coque', utility: 'Utilitaire', jump: 'Saut' };
const FAMILY_LABEL: Record<string, string> = { laser: 'canons', missile: 'missiles', swarm: 'nuée' };
const credits = (n: number) => `${n.toLocaleString('fr-FR')} ¢`;

export class Menus {
  private view: MenuView | null = null;

  constructor(private readonly root: HTMLElement, private readonly data: () => GameData, private readonly onAction: (action: MenuAction, value?: string) => void) {
    root.addEventListener('click', (e) => {
      const target = (e.target as HTMLElement).closest<HTMLElement>('[data-action]');
      if (!target || target.hasAttribute('disabled')) return;
      this.onAction(target.dataset.action!, target.dataset.value);
    });
    root.addEventListener('input', (e) => {
      const target = e.target as HTMLInputElement;
      if (target.dataset.setting) this.onAction('setting', `${target.dataset.setting}=${target.type === 'checkbox' ? target.checked : target.value}`);
    });
    root.addEventListener('change', (e) => {
      const target = e.target as HTMLSelectElement;
      if (target.tagName === 'SELECT' && target.dataset.setting) this.onAction('setting', `${target.dataset.setting}=${target.value}`);
    });
    // Formulaires (nom du pilote, signalement) : Entrée dans le champ ou bouton Valider.
    root.addEventListener('submit', (e) => {
      e.preventDefault();
      const form = e.target as HTMLFormElement;
      const field = form.querySelector<HTMLInputElement | HTMLTextAreaElement>('[name="value"]');
      if (form.dataset.form && field) this.onAction(form.dataset.form, field.value);
    });
    root.addEventListener('mousemove', (e) => {
      const item = (e.target as HTMLElement).closest<HTMLElement>('.mi');
      if (item && document.activeElement !== item) item.focus({ preventScroll: true });
    });
  }

  get current(): MenuView | null { return this.view; }

  show(view: MenuView): void {
    const previousFocus = (document.activeElement as HTMLElement | null)?.dataset?.focusKey;
    this.view = view;
    this.root.innerHTML = this.render(view);
    this.root.className = `menu-root view-${view.kind}`;
    this.root.hidden = false;
    const again = previousFocus ? this.root.querySelector<HTMLElement>(`[data-focus-key="${previousFocus}"]`) : null;
    (again ?? this.root.querySelector<HTMLElement>('.mi[data-default]') ?? this.root.querySelector<HTMLElement>('.mi'))?.focus({ preventScroll: true });
  }

  hide(): void {
    this.view = null;
    this.root.hidden = true;
    this.root.innerHTML = '';
  }

  /** Navigation clavier/manette entre les éléments focalisables. */
  move(delta: number): void {
    const items = [...this.root.querySelectorAll<HTMLElement>('.mi:not([disabled])')];
    if (items.length === 0) return;
    const index = items.indexOf(document.activeElement as HTMLElement);
    const next = items[(index + delta + items.length) % items.length]!;
    next.focus({ preventScroll: true });
  }

  confirm(): void {
    const el = document.activeElement as HTMLElement | null;
    if (!el || !this.root.contains(el)) return;
    if (el instanceof HTMLInputElement && el.type === 'checkbox') {
      el.checked = !el.checked;
      el.dispatchEvent(new Event('input', { bubbles: true }));
    } else {
      el.click();
    }
  }

  /** Flèches gauche/droite : réglages et choix d'appareil. */
  adjust(delta: number): void {
    const el = document.activeElement as HTMLElement | null;
    if (!el || !this.root.contains(el)) return;
    if (el instanceof HTMLInputElement && el.type === 'range') {
      const step = Number(el.step) || 1;
      el.value = String(Math.min(Number(el.max), Math.max(Number(el.min), Number(el.value) + delta * step)));
      el.dispatchEvent(new Event('input', { bubbles: true }));
    } else if (el instanceof HTMLSelectElement) {
      el.selectedIndex = Math.min(el.options.length - 1, Math.max(0, el.selectedIndex + delta));
      el.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (this.view?.kind === 'dock') {
      this.onAction(this.view.tab === 'hangar' ? 'cycleShip' : 'cycleTab', String(delta));
    }
  }

  /** État du service à quai, mis à jour sans reconstruire le menu (le focus reste en place). */
  setDockStatus(text: string): void {
    const el = this.root.querySelector<HTMLElement>('#dock-status');
    if (el && el.textContent !== text) el.textContent = text;
  }

  private render(view: MenuView): string {
    switch (view.kind) {
      case 'loading':
        return `<div class="panel center"><div class="brand">SPACE ARENA</div><p class="muted">${esc(view.text)}</p></div>`;
      case 'error':
        return `<div class="panel center error"><div class="brand">SPACE ARENA</div><pre>${esc(view.text)}</pre></div>`;
      case 'title':
        return `<div class="panel left">
          <div class="brand">SPACE ARENA</div>
          <div class="tag">Prototype web · combat spatial en arène</div>
          <nav>
            ${item('startDock', this.data().station.label, 'Partir amarré à la station : missions, armurerie complète, hangar', true, 'start-station', 'station')}
            ${item('startDock', this.data().carrier.label, 'Partir amarré au porteur : missions, arsenal limité', false, 'start-carrier', 'carrier')}
            ${item('options', 'Options')}
            ${item('help', 'Commandes')}
            ${item('changePilot', 'Changer de pilote', `Pilote actuel : ${view.player}`)}
          </nav>
          <div class="foot muted">Version ${esc(__BUILD_INFO__.version)} · build ${esc(__BUILD_INFO__.commit)}${__BUILD_INFO__.dirty ? ' (modifié)' : ''}</div>
        </div>`;
      case 'pilot':
        return `<div class="panel center">
          <div class="title">NOM DU PILOTE</div>
          <p class="muted">Il accompagne la télémétrie de vos parties : déroulé, contrôles, missions, machine. Il sert à relier un retour ou un bug à son auteur.</p>
          <form data-form="setPilot" class="form">
            <input class="mi text" type="text" name="value" maxlength="32" autocomplete="nickname" spellcheck="false" value="${esc(view.name)}" data-focus-key="pilot-name" data-default placeholder="Votre nom ou pseudo" required>
            <nav class="row"><button class="mi" type="submit" data-focus-key="pilot-ok"><span>Valider</span></button>${view.first ? '' : item('back', 'Annuler')}</nav>
          </form>
        </div>`;
      case 'report':
        return `<div class="panel center">
          <div class="title">SIGNALER UN PROBLÈME</div>
          <p class="muted">Décrivez ce qui s’est passé et ce que vous attendiez. L’état du jeu à cet instant, votre machine, vos réglages et le journal de la partie sont joints automatiquement.</p>
          <form data-form="sendReport" class="form">
            <textarea class="mi text" name="value" rows="6" maxlength="4000" data-focus-key="report-text" data-default placeholder="Ex. : le boss est resté bloqué dans un rocher pendant la vague 5" required></textarea>
            <nav class="row"><button class="mi" type="submit" data-focus-key="report-send"><span>Envoyer</span></button>${item('back', 'Annuler')}</nav>
          </form>
        </div>`;
      case 'dock':
        return this.renderDock(view);
      case 'paused':
        return `<div class="panel left">
          <div class="title">PAUSE</div>
          <nav>
            ${item('resume', 'Reprendre', '', true)}
            ${item('options', 'Options')}
            ${item('help', 'Commandes')}
            ${item('report', 'Signaler un problème')}
            ${item('quit', 'Quitter vers le titre')}
          </nav>
        </div>`;
      case 'confirmQuit':
        return `<div class="panel center">
          <div class="title">Quitter la partie ?</div>
          <p class="muted">${view.missionActive
            ? `La mission en cours sera abandonnée ; ${Math.round(this.data().equipment.economy.abandonFactor * 100)} % des crédits du score sont conservés.`
            : 'Votre profil, vos crédits et votre équipement sont conservés.'}</p>
          <nav class="row">${item('confirmQuit', 'Quitter')}${item('back', 'Annuler', '', true)}</nav>
        </div>`;
      case 'options':
        return this.renderOptions(view.settings);
      case 'help':
        return this.renderHelp(view.keys);
      case 'results':
        return this.renderResults(view);
    }
  }

  private renderDock(view: Extract<MenuView, { kind: 'dock' }>): string {
    const labels: Record<DockTab, string> = { hangar: 'Hangar', armory: view.dock === 'station' ? 'Armurerie' : 'Arsenal', missions: 'Missions' };
    const tab = (id: DockTab) =>
      `<button class="mi tab ${view.tab === id ? 'active' : ''}" data-action="dockTab" data-value="${id}" data-focus-key="tab-${id}"><span>${labels[id]}</span></button>`;
    const body = view.tab === 'hangar' ? this.renderHangar(view) : view.tab === 'armory' ? this.renderArmory(view) : this.renderMissions(view);
    const dockData = view.dock === 'station' ? this.data().station : this.data().carrier;
    const launchHint = view.mission.active ? `Reprendre : ${view.mission.label}` : 'Vol libre, sans mission';
    return `<div class="panel left wide station">
      <div class="station-head"><div class="title">${esc(dockData.label.toUpperCase())}</div><div class="credits">${credits(view.profile.credits)}</div></div>
      <p class="muted small" id="dock-status"></p>
      <nav class="row actions">${item('launch', 'Décoller', launchHint, view.tab !== 'missions' || view.mission.active)}${item('options', 'Options')}${item('help', 'Commandes')}${item('report', 'Signaler un problème')}${item('quit', 'Quitter vers le titre')}</nav>
      ${view.message ? `<p class="station-msg">${esc(view.message)}</p>` : ''}
      <nav class="row tabs">${dockTabs[view.dock].map(tab).join('')}</nav>
      ${body}
    </div>`;
  }

  private renderHangar(view: Extract<MenuView, { kind: 'dock' }>): string {
    const data = this.data();
    const bar = (label: string, value: number, max: number, text: string) =>
      `<div class="stat"><span>${label}</span><div class="bar"><i style="width:${Math.round(Math.min(1, value / max) * 100)}%"></i></div><b>${text}</b></div>`;
    const cards = shipIds.map((id) => {
      const s = data.ships.ships[id];
      const st = loadoutStats(data, id, view.profile.loadouts[id]);
      return `<button class="mi ship ${id === view.ship ? 'selected' : ''}" data-action="pickShip" data-value="${id}" data-focus-key="ship-${id}">
        <div class="ship-name">${esc(s.label)}</div><div class="ship-role">${esc(s.role)}</div>
        ${bar('Coque', st.hull, 190, String(st.hull))}
        ${bar('Bouclier', st.shield, 90, String(st.shield))}
        ${bar('Vitesse', st.boost, 240, `${st.cruise}/${st.boost} m/s`)}
        ${bar('Agilité', st.agility, 1, `${Math.round(st.agility * 100)} %`)}
        ${st.laserDps > 0 ? bar('Canons', st.laserDps, 120, `${Math.round(st.laserDps)} dég./s`) : bar('Canons', 0, 1, 'aucun')}
        ${bar('Missiles', st.missiles, 18, String(st.missiles))}
        ${st.swarmBurst > 0 ? bar('Nuée', st.swarmBurst, 14, `salves de ${st.swarmBurst}`) : ''}
      </button>`;
    }).join('');
    const loadout = view.profile.loadouts[view.ship];
    const slots = slotIds.filter((slot) => slotAvailable(data, view.ship, slot)).map((slot) => `<div><span>${this.slotLabel(view.ship, slot)}</span><b>${esc(data.equipment.items[loadout[slot]]?.label ?? '—')}</b></div>`).join('');
    const st = view.profile.stats;
    return `<div class="ships">${cards}</div>
      <div class="loadout">${slots}</div>
      <p class="muted small">Sorties ${st.sorties} · victoires ${st.victories} · meilleur score ${st.bestScore.toLocaleString('fr-FR')} · crédits gagnés ${credits(st.creditsEarned)}</p>
      <p class="muted small">${view.mission.active ? 'Mission en cours : changement d’appareil impossible avant sa fin.' : '← → : changer d’appareil · l’équipement se règle dans l’armurerie.'}</p>`;
  }

  private renderArmory(view: Extract<MenuView, { kind: 'dock' }>): string {
    const data = this.data();
    const loadout = view.profile.loadouts[view.ship];
    const stocked = (id: string) => view.dock === 'station' || data.equipment.items[id]!.price === 0 || data.equipment.carrierStock.includes(id) || loadout[view.slot] === id;
    const slots = slotIds.filter((slot) => slotAvailable(data, view.ship, slot)).map((slot) => `<button class="mi slot ${slot === view.slot ? 'active' : ''}" data-action="armorySlot" data-value="${slot}" data-focus-key="slot-${slot}">
        <span>${this.slotLabel(view.ship, slot)}</span><small>${esc(data.equipment.items[loadout[slot]]?.label ?? '—')}</small></button>`).join('');
    const items = compatibleItems(data, view.ship, view.slot).filter(([id]) => stocked(id)).map(([id, it]) => {
      const equipped = loadout[view.slot] === id;
      const owned = owns(view.profile, data, id);
      const status = equipped ? '<em class="ok">MONTÉ</em>' : owned ? '<em>MONTER</em>' : `<em class="${view.profile.credits >= it.price ? 'buy' : 'poor'}">${credits(it.price)}</em>`;
      const deltas = equipped ? [] : itemDeltas(data, view.ship, loadout, view.slot, id);
      const deltaText = deltas.map((d) => `<i class="${d.sign > 0 ? 'up' : 'down'}">${esc(d.label)} ${esc(d.from)} → ${esc(d.to)}</i>`).join('');
      return `<button class="mi item ${equipped ? 'equipped' : ''}" data-action="armoryItem" data-value="${id}" data-focus-key="item-${id}">
        <div class="item-head"><span>${esc(it.label)}</span>${status}</div>
        <small>${esc(it.description)}</small>
        ${deltaText ? `<div class="deltas">${deltaText}</div>` : ''}
      </button>`;
    }).join('');
    const note = view.dock === 'station'
      ? 'objets achetés une fois, montables sur tout châssis compatible'
      : 'arsenal limité du porteur : l’armurerie complète est à la station';
    return `<div class="tag">${esc(data.ships.ships[view.ship].label)} · ${note}</div>
      <div class="armory"><div class="slots">${slots}</div><div class="items">${items}</div></div>`;
  }

  private renderMissions(view: Extract<MenuView, { kind: 'dock' }>): string {
    const data = this.data();
    const e = data.equipment.economy;
    const rate = `${Number((e.scoreRate * 100).toFixed(1)).toLocaleString('fr-FR')} % du score`;
    if (view.mission.active) {
      return `<div class="tag">Mission en cours : ${esc(view.mission.label)} · ${esc(view.mission.progress)}</div>
        <p class="muted small">Une seule mission à la fois : terminez-la ou abandonnez-la avant d’en accepter une autre. Le service du dock répare et ravitaille pendant que vous êtes à quai.</p>
        <nav>${item('abandonMission', 'Abandonner la mission', `${Math.round(e.abandonFactor * 100)} % des crédits du score conservés, les ennemis se replient`)}</nav>`;
    }
    const all = data.missions.order.map((id) => [id, data.missions.scenarios[id]!] as const);
    // Missions de la zone proposées à ce dock ; depuis le porteur, aussi celles des autres zones (saut d'abord).
    const reachable = jumpTargets(data, view.zone, 'carrier');
    const zoneHere = data.zones.zones[view.zone];
    const offered = (s: (typeof all)[number][1]) => s.docks.includes(view.dock) && (s.zone === view.zone || (view.dock === 'carrier' && reachable.includes(s.zone)));
    const here = all.filter(([, s]) => offered(s));
    const elsewhere = all.filter(([, s]) => !offered(s));
    const zoneLabel = (id: string) => data.zones.zones[id]?.label ?? id;
    const buttons = here.map(([id, s], i) => {
      const jump = s.zone !== view.zone ? `${data.zones.zones[s.zone]?.kind === 'surface' ? 'Descente vers' : zoneHere?.kind === 'surface' ? 'Remontée vers' : 'Saut vers'} ${zoneLabel(s.zone)} · ` : '';
      const label = s.zone !== view.zone ? `${s.label} ⟶ ${zoneLabel(s.zone)}` : s.label;
      return item('acceptMission', label, `${jump}${s.description} · ${rate} + prime ${credits(s.victoryBonus)}`, i === 0, `mission-${id}`, id);
    }).join('');
    const home = data.zones.home;
    const back = view.dock !== 'carrier' ? ''
      : zoneHere?.kind === 'surface' && zoneHere.orbitOf
        ? item('jump', `Remonter en orbite : ${zoneLabel(zoneHere.orbitOf)}`, 'Remontée du porteur sans mission', false, 'jump-orbit', zoneHere.orbitOf)
        : view.zone !== home && reachable.includes(home)
          ? item('jump', `Retour à ${zoneLabel(home)}`, 'Saut du porteur sans mission : station, armurerie complète et hangar', false, 'jump-home', home)
          : '';
    return `<div class="tag">Zone : ${esc(zoneLabel(view.zone))} · appareil : ${esc(data.ships.ships[view.ship].label)} · la mission commence au décollage</div>
      <nav>${buttons}${back}</nav>
      ${elsewhere.length ? `<p class="muted small">Proposées ailleurs : ${elsewhere.map(([, s]) => {
        const zone = data.zones.zones[s.zone];
        const where = zone?.kind === 'surface' && zone.orbitOf ? `descente depuis ${zoneLabel(zone.orbitOf)}` : s.docks.map((d) => data[d].label).join(', ');
        return `${esc(s.label)} (${esc(where)})`;
      }).join(', ')}.</p>` : ''}
      <p class="muted small">Défaite : ${Math.round(e.defeatFactor * 100)} % des crédits du score · abandon : ${Math.round(e.abandonFactor * 100)} %. Abattu, vous réapparaissez au dernier dock quitté.</p>`;
  }

  private slotLabel(ship: ShipId, slot: SlotId): string {
    const family = slotFamily(this.data(), ship, slot);
    return slot === 'primary' || slot === 'secondary' ? `${SLOT_LABEL[slot]} · ${FAMILY_LABEL[family] ?? family}` : SLOT_LABEL[slot];
  }

  private renderOptions(s: Settings): string {
    const check = (key: keyof Settings, label: string, hint = '') =>
      `<label class="opt"><span>${label}${hint ? `<small>${hint}</small>` : ''}</span><input class="mi" type="checkbox" data-setting="${key}" data-focus-key="opt-${key}-i" ${s[key] ? 'checked' : ''}></label>`;
    const quality = (['low', 'medium', 'high', 'epic'] as Quality[]).map((q) => `<option value="${q}" ${s.quality === q ? 'selected' : ''}>${{ low: 'Basse', medium: 'Moyenne', high: 'Haute', epic: 'Épique' }[q]}</option>`).join('');
    return `<div class="panel left wide">
      <div class="title">OPTIONS</div>
      <div class="options">
        <label class="opt"><span>Sensibilité souris <small>${s.mouseSensitivity.toFixed(2)}</small></span>
          <input class="mi" type="range" data-setting="mouseSensitivity" data-focus-key="opt-sens" min="${SENSITIVITY_MIN}" max="${SENSITIVITY_MAX}" step="${SENSITIVITY_STEP}" value="${s.mouseSensitivity}"></label>
        ${check('invertPitch', 'Souris haut = nez haut', 'réglage par défaut du prototype')}
        <label class="opt"><span>Pilotage à la souris <small>manche virtuel = le curseur fixe la vitesse de virage · direct = visée type FPS (Unreal) · plafonné = direct au taux max</small></span>
          <select class="mi" data-setting="mouseMode" data-focus-key="opt-mouse"><option value="joystick" ${s.mouseMode === 'joystick' ? 'selected' : ''}>Manche virtuel</option><option value="direct" ${s.mouseMode === 'direct' ? 'selected' : ''}>Directe</option><option value="capped" ${s.mouseMode === 'capped' ? 'selected' : ''}>Plafonnée</option></select></label>
        <label class="opt"><span>Accélérateur <small>persistant = Z/S règlent la poussée qui reste en place (molette, X pour couper) · tenu = poussée tant que la touche est enfoncée</small></span>
          <select class="mi" data-setting="throttleMode" data-focus-key="opt-throttle"><option value="throttle" ${s.throttleMode === 'throttle' ? 'selected' : ''}>Persistant</option><option value="hold" ${s.throttleMode === 'hold' ? 'selected' : ''}>Touche tenue</option></select></label>
        <label class="opt"><span>Qualité graphique</span><select class="mi" data-setting="quality" data-focus-key="opt-quality">${quality}</select></label>
        ${check('hudEnhanced', 'HUD renforcé')}
        ${check('sound', 'Son')}
        <label class="opt"><span>Volume <small>${Math.round(s.volume * 100)} %</small></span>
          <input class="mi" type="range" data-setting="volume" data-focus-key="opt-volume" min="0" max="1" step="0.05" value="${s.volume}"></label>
        ${check('reducedFlashes', 'Flashes réduits', 'ne change ni les dégâts ni la difficulté')}
        ${check('reducedCameraMotion', 'Mouvements de caméra réduits')}
        ${check('shareTelemetry', 'Partager la télémétrie', 'envoie le résumé et le journal de chaque partie (anonyme) pour régler le jeu')}
      </div>
      <nav class="row">${item('replayTutorial', 'Revoir le tutoriel')}${item('exportTelemetry', 'Exporter la télémétrie')}${item('back', 'Retour', '', true)}</nav>
    </div>`;
  }

  private renderHelp(keys: Record<string, string>): string {
    const rows: [string, string, string][] = [
      ['Accélérateur + / −', `${keys.forward} / ${keys.backward} · molette`, 'Stick gauche vertical'],
      ['Couper les gaz', keys.throttleZero ?? 'X', '—'],
      ['Glissement latéral', `${keys.left} / ${keys.right}`, 'Stick gauche horizontal'],
      ['Monter / descendre', `${keys.up} / ${keys.down}`, 'Y / X'],
      ['Lacet et tangage', 'Souris', 'Stick droit'],
      ['Roulis', `${keys.rollLeft} / ${keys.rollRight}`, 'LB / RB'],
      ['Boost / frein', `${keys.boost} / ${keys.brake}`, 'Clic stick gauche / droit'],
      ['Arme primaire / secondaire', 'Clic gauche / droit', 'RT / LT'],
      ['Pause', 'Échap / P', 'Start'],
      ['Écourter l’intermission', 'N', 'Croix droite'],
      ['Saut vers une autre zone (module de saut, hors mission)', keys.jump ?? 'J', 'Select'],
      ['Passer le tutoriel', 'T', 'Croix gauche'],
      ['Recommencer (pause, résultats)', 'R', 'B'],
    ];
    return `<div class="panel left wide">
      <div class="title">COMMANDES</div>
      <table class="keys"><tr><th></th><th>Clavier / souris</th><th>Manette</th></tr>
      ${rows.map(([a, k, g]) => `<tr><td>${esc(a)}</td><td>${esc(k)}</td><td>${esc(g)}</td></tr>`).join('')}</table>
      <p class="muted small"><b>Docks</b> : la station (repère orange) et le porteur (repère vert). Entrez dans la zone d'approche derrière la poupe, sous 55 m/s et aligné sur le tunnel (anneaux verts) pour vous amarrer. À quai : réparation et ravitaillement, menu du dock (missions, armurerie ; hangar à la station). Une mission choisie à quai commence au décollage. Abattu, vous réapparaissez au dernier dock quitté ; en mission, dans la limite des appareils en réserve.</p>
      <p class="muted small"><b>Zones</b> : le porteur peut sauter vers une autre zone, comme la Forteresse Carmin (mission de siège : détruire ses tourelles). Choisissez la mission depuis son menu ; le retour se fait aussi depuis le porteur.</p>
      <p class="muted small"><b>Bouclier</b> : barre bleue au-dessus de la coque. Il absorbe les dégâts et se recharge après quelques secondes sans être touché ; rompu, il met plus longtemps à revenir. <b>Crédits</b> : chaque mission rapporte des crédits, à dépenser à l’armurerie de la station (le porteur n’a qu’un arsenal limité).</p>
      <p class="muted small"><b>Boost</b> : jauge bleue à droite du réticule. <b>Laser</b> : jauge de chaleur à gauche ; en surchauffe, il se coupe.</p>
      <p class="muted small">Les touches suivent la position physique : Z/Q/S/D en AZERTY, W/A/S/D en QWERTY. Sur l'HYDRA, le clic gauche lance un missile, le clic droit une salve de nuée.</p>
      <nav class="row">${item('back', 'Retour', '', true)}</nav>
    </div>`;
  }

  private renderResults(view: Extract<MenuView, { kind: 'results' }>): string {
    const s = view.summary;
    const kills = Object.values(s.kills).reduce((a, b) => a + b, 0);
    const received = Object.values(s.damageReceived).reduce((a, b) => a + b, 0);
    const accuracy = s.laserShots > 0 ? `${Math.round((s.laserHitsEnemy / s.laserShots) * 100)} %` : '—';
    const outcome = s.outcome === 'victory' ? 'VICTOIRE' : s.outcome === 'defeat' ? 'DÉFAITE' : 'ABANDON';
    const minutes = Math.floor(s.simSeconds / 60), seconds = Math.round(s.simSeconds % 60);
    const swarm = s.swarmLaunched > 0
      ? `<tr><td>Nuée</td><td>${s.swarmLaunched} lancés · ${s.swarmDirectHits} impacts directs · ${s.swarmProximity} fusées de proximité · ${s.swarmTargetLosses} pertes</td></tr>` : '';
    return `<div class="panel left wide">
      <div class="title ${s.outcome}">${outcome}</div>
      <div class="tag">${esc(this.data().missions.scenarios[view.mode]?.label ?? view.mode)} · ${esc(this.data().ships.ships[view.ship].label)} · ${minutes} min ${String(seconds).padStart(2, '0')} s</div>
      <table class="results">
        <tr><td>Score</td><td><b>${s.score.toLocaleString('fr-FR')}</b> · multiplicateur max ×${Number(s.bestMultiplier.toFixed(2))}</td></tr>
        <tr><td>Destructions</td><td>${kills} (${Object.entries(s.kills).map(([k, n]) => `${n} ${k}`).join(', ') || 'aucune'})</td></tr>
        <tr><td>Précision laser</td><td>${accuracy} (${s.laserHitsEnemy}/${s.laserShots})</td></tr>
        <tr><td>Dégâts infligés</td><td>laser ${Math.round(s.damageDealt.laser)} · missiles ${Math.round(s.damageDealt.missile)} · nuée ${Math.round(s.damageDealt.swarm)}</td></tr>
        <tr><td>Dégâts reçus</td><td>${Math.round(received)} (${Object.entries(s.damageReceived).map(([k, n]) => `${k} ${Math.round(n)}`).join(', ') || 'aucun'})</td></tr>
        ${swarm}
        <tr><td>Vagues</td><td>${s.waveDurations.map((d, i) => `V${i + 1} ${Math.round(d)} s`).join(' · ') || '—'}</td></tr>
        <tr><td>Porteur</td><td>${s.docks} appontage(s) · ${Math.round(s.serviceSeconds)} s de service · ${s.deaths} appareil(s) perdu(s)</td></tr>
        <tr><td>Bouclier</td><td>${Math.round(s.shieldAbsorbed)} dégâts absorbés · ${s.shieldBreaks} rupture(s) · ${s.emergencyRepairs} réparation(s) d'urgence</td></tr>
        <tr><td>Recul</td><td>${Math.round(s.flight.reverseRatio * 100)} % du temps mobile · ${s.overheats} surchauffe(s)</td></tr>
        ${this.rewardRow(view)}
      </table>
      <nav class="row">${item('continue', 'Continuer', 'Retour en vol ; nouvelle mission depuis un dock', true)}${item('exportLast', 'Exporter la session')}</nav>
    </div>`;
  }

  private rewardRow(view: Extract<MenuView, { kind: 'results' }>): string {
    const r = view.reward;
    if (!r) return '';
    const detail = [`score ${credits(r.fromScore)}`, r.bonus > 0 ? `prime de victoire ${credits(r.bonus)}` : ''].filter(Boolean).join(' + ');
    return `<tr class="reward"><td>Crédits</td><td><b>+${credits(r.total)}</b> (${detail}) · solde ${credits(view.credits)}</td></tr>`;
  }
}

function item(action: string, label: string, hint = '', isDefault = false, focusKey = action, value = ''): string {
  return `<button class="mi" data-action="${action}" data-focus-key="${focusKey}" ${value ? `data-value="${value}"` : ''} ${isDefault ? 'data-default' : ''}><span>${esc(label)}</span>${hint ? `<small>${esc(hint)}</small>` : ''}</button>`;
}
